import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from "react";
// Fix BT (2026-09-09): Plus/Trash2 para el editor manual, RefreshCw para releer
// las plantillas sin cerrar la ventana.
import { X, Loader2, Wand2, Check, ListChecks, Plus, Trash2, RefreshCw } from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { useCerrarConAtras } from "../../utils/useCerrarConAtras";
import { opts } from "./constants";

// CEN 2.0 FASE 2 sub-entrega 2 — Asignación asistida de desglose de tareas por
// ítem del presupuesto. Aprendizaje del bug de MapeoMORubroModal (iteration_165):
// NUNCA pre-seleccionar sugerencias al cargar — `selected` arranca en `null`
// (sin cambios) para TODAS las filas; el usuario decide fila por fila o usa el
// botón explícito "Aceptar sugerencias de alta confianza".
function scoreBadge(status) {
  if (status === "alta") return "bg-emerald-100 text-emerald-700";
  if (status === "media") return "bg-amber-100 text-amber-700";
  if (status === "baja") return "bg-gray-100 text-gray-500";
  return "bg-gray-50 text-gray-400";
}

// Fix BT (2026-09-09): "se tiene que poder seleccionar mas de una tarea y definir
// la carga porcentual" (Edson, 09/09). Es lo que trae el Excel de desglose: por
// ítem, hasta cinco tareas con su %. El backend ya lo acepta (mappings con
// {tarea_numero, pct}) y ya valida que sume ~100%; lo único que faltaba era dónde
// cargarlo. Va EN LÍNEA, dentro de la celda: una ventana encima de esta ventana es
// lo que rompió el autollenado en el Fix AP.
function EditorTareasManual({ catalogo, valor, onChange, onNuevaTarea, onFinalizar, finalizando }) {
  const filas = valor || [];
  const suma = filas.reduce((s, f) => s + (Number(f.pct_ui) || 0), 0);
  const ok = Math.abs(suma - 100) <= 2;

  const setFila = (i, campo, v) => onChange(filas.map((f, j) => j === i ? { ...f, [campo]: v } : f));
  const agregar = () => onChange([...filas, { tarea_numero: "", pct_ui: "" }]);
  const quitar = (i) => onChange(filas.filter((_, j) => j !== i));
  const repartir = () => {
    if (filas.length === 0) return;
    const base = Math.floor(100 / filas.length);
    onChange(filas.map((f, i) => ({ ...f, pct_ui: i === filas.length - 1 ? 100 - base * (filas.length - 1) : base })));
  };

  return (
    <div className="mt-1.5 border border-[#2E6DA4]/30 bg-[#EBF3FB]/40 rounded-lg p-2 space-y-1.5" data-testid="asignar-tareas-editor-manual">
      {filas.map((f, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <select value={f.tarea_numero} onChange={e => setFila(i, "tarea_numero", e.target.value)}
            className="flex-1 min-w-0 border border-gray-200 rounded px-1.5 py-1 text-[11px] bg-white"
            data-testid={`asignar-tareas-manual-tarea-${i}`}>
            <option value="">— Tarea —</option>
            {catalogo.map(c => <option key={c.numero} value={c.numero}>{c.numero}. {c.nombre}</option>)}
          </select>
          <input type="number" min={0} max={100} value={f.pct_ui}
            onChange={e => setFila(i, "pct_ui", e.target.value)}
            className="w-14 border border-gray-200 rounded px-1 py-1 text-[11px] text-right bg-white"
            data-testid={`asignar-tareas-manual-pct-${i}`} />
          <span className="text-[10px] text-gray-400">%</span>
          <button type="button" onClick={() => quitar(i)} className="p-1 text-gray-400 hover:text-red-600"
            data-testid={`asignar-tareas-manual-quitar-${i}`}><Trash2 size={12} /></button>
        </div>
      ))}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button type="button" onClick={agregar} className="flex items-center gap-1 text-[11px] font-semibold text-[#2E6DA4] hover:underline"
            data-testid="asignar-tareas-manual-agregar"><Plus size={11} /> Agregar tarea</button>
          {filas.length > 1 && (
            <button type="button" onClick={repartir} className="text-[11px] font-semibold text-[#2E6DA4] hover:underline"
              data-testid="asignar-tareas-manual-repartir">Repartir parejo</button>
          )}
          {/* Fix BY (2026-09-09): "que se puedan agregar tareas nuevas y que siempre
              vayan numeradas" (Edson, 09/09). El nombre se pide con el prompt del
              navegador A PROPÓSITO: una ventana propia acá sería una ventana dentro
              de otra ventana, que es lo que rompió el autollenado en el Fix AP. */}
          <button type="button" onClick={async () => {
              const nombre = window.prompt("Nombre de la tarea nueva. El número se asigna solo:");
              if (!nombre || !nombre.trim()) return;
              const numero = await onNuevaTarea(nombre.trim());
              if (numero) onChange([...filas, { tarea_numero: String(numero), pct_ui: "" }]);
            }} className="text-[11px] font-semibold text-emerald-700 hover:underline"
            data-testid="asignar-tareas-manual-nueva-tarea">+ Tarea nueva</button>
        </div>
        <span className={`text-[11px] font-bold ${ok ? "text-emerald-700" : "text-red-600"}`} data-testid="asignar-tareas-manual-suma">
          {Math.round(suma * 100) / 100}%
        </span>
      </div>
      {!ok && <p className="text-[10px] text-red-600">La suma tiene que dar 100% para poder guardar este ítem.</p>}
      {/* Fix IJ (2026-10-30): "veo que ya das la opción de editar, falta un botón de finalizar y aplicar" (CEN). Guarda
          solo este ítem, sin esperar a «Confirmar y guardar». */}
      {onFinalizar && (
        <div className="flex justify-end">
          <button type="button" onClick={onFinalizar} disabled={!ok || finalizando || filas.some(f => f.tarea_numero === "")}
            className="px-2.5 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold disabled:opacity-50"
            data-testid="asignar-tareas-manual-finalizar">{finalizando ? "Aplicando..." : "Finalizar y aplicar"}</button>
        </div>
      )}
    </div>
  );
}

const PAGE_SIZE = 50;

// Fix IB (2026-10-28): "que las sugerencias sean solo tareas existentes en el desplegable" (CEN). Texto de un desglose
// con los nombres del catálogo (el mismo del desplegable): «8. BANDEJADO EN PLANTA 100%».
function textoTareas(tareas, catalogo) {
  return (tareas || [])
    .map(t => `${t.tarea_numero}. ${(catalogo.find(c => String(c.numero) === String(t.tarea_numero)) || {}).nombre || ""} ${Math.round((Number(t.pct) || 0) * 10000) / 100}%`)
    .join(" · ");
}

export function AsignarTareasModal({ open, budgetId, onClose, onPatchItems }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [finalizando, setFinalizando] = useState(null);   // Fix IJ: «Finalizar y aplicar» de un ítem
  const [rows, setRows] = useState([]); // {item_uid, numero, descripcion, tareas_actuales, sugerido, match_status, selected}
  const [templates, setTemplates] = useState([]);
  // Fix BT (2026-09-09): catálogo global de tareas (los 33 rubros) para el editor
  // manual, y las tareas cargadas a mano por ítem. Se guardan aparte del `selected`
  // del <select> para no ensuciar su valor.
  const [catalogoTareas, setCatalogoTareas] = useState([]);
  const [manual, setManual] = useState({});
  // Fix BY (2026-09-09): configuración copiada de un ítem, lista para pegar en
  // otros. Vive solo mientras la ventana está abierta: no se guarda nada hasta
  // que el usuario confirme.
  const [copiado, setCopiado] = useState(null);
  // Fix IB (2026-10-28): "poder eliminar todas las asignadas ... ahora solo se puede eliminar uno a uno" (CEN). Ítems
  // marcados en bloque para quedar sin tareas; se borran recién con «Confirmar y guardar» y se puede deshacer antes.
  const [quitados, setQuitados] = useState(null);
  // Fix ID (2026-10-29): "¿está usando las asignaciones de Jejui e Invicta como aprendizaje?" (CEN). De dónde salen las
  // sugerencias y el panel para elegir qué presupuestos enseñan (vacío = todos los que tienen tareas).
  const [referencias, setReferencias] = useState(null);
  const [refsPanel, setRefsPanel] = useState(null);   // null = cerrado; {opciones, seleccion, guardando}
  const [filterStatus, setFilterStatus] = useState("sin_tareas");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, { data: tmpl }, { data: cat }] = await Promise.all([
        axios.get(`${API}/budgets/${budgetId}/match-tareas`, opts),
        axios.get(`${API}/catalogo-tareas/templates`, opts),
        // Fix BT (2026-09-09): el catálogo de tareas es el mismo contra el que la
        // pestaña Tareas de la obra resuelve los nombres, así que lo que se cargue
        // acá se ve allá sin ninguna traducción.
        axios.get(`${API}/catalogo-tareas`, opts),
      ]);
      setRows((data.items || []).map(r => ({ ...r, selected: null })));
      setReferencias(data.referencias || null);   // Fix ID
      setTemplates(tmpl || []);
      setCatalogoTareas(cat || []);
      setManual({});
      setQuitados(null);   // Fix IB
      setPage(0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cargar sugerencias de tareas");
    } finally { setLoading(false); }
  }, [budgetId]);

  useEffect(() => { if (open) fetchData(); }, [open, fetchData]);

  // Fix AI (2026-09-04): el botón "atrás" del navegador cierra este modal.
  useCerrarConAtras(open, onClose);

  const filtered = useMemo(() => rows.filter(r => {
    const matchStatusF = filterStatus === "todos" ? true
      : filterStatus === "sin_tareas" ? (r.tareas_actuales || []).length === 0
      : filterStatus === "con_tareas" ? (r.tareas_actuales || []).length > 0   // Fix IB
      : r.match_status === filterStatus;
    // Fix IJ (2026-10-30): "que reconozca el nro del ítem a la hora de buscar" (CEN): «4.1.1» encuentra 4.1.1 y sus sub-ítems.
    const q = search.trim().toLowerCase();
    const matchSearch = !q || (r.descripcion || "").toLowerCase().includes(q) || String(r.numero || "").toLowerCase().startsWith(q);
    return matchStatusF && matchSearch;
  }), [rows, filterStatus, search]);

  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  // Fix IK (2026-10-30): "que se mantenga a la vista el subítem principal («TORRE DE VIVIENDA») con «NIVEL 5» y a medida
  // que se va bajando se oculta el NIVEL 5 cuando el NIVEL 7 le alcanza" (CEN). Debajo de los títulos de columna queda la
  // ruta de encabezados de la fila que está arriba de todo; cambia sola al desplazar.
  const listaRef = useRef(null);
  const [contexto, setContexto] = useState([]);
  const actualizarContexto = useCallback(() => {
    const el = listaRef.current;
    if (!el) return;
    const cab = el.querySelector("thead tr");
    let ruta = [];
    if (el.scrollTop > 0 && cab) {
      const y = cab.getBoundingClientRect().bottom + 2;
      const fila = [...el.querySelectorAll("tbody tr[data-ruta]")].find(f => f.getBoundingClientRect().bottom > y);
      if (fila) ruta = JSON.parse(fila.getAttribute("data-ruta") || "[]");
    }
    setContexto(prev => (JSON.stringify(prev) === JSON.stringify(ruta) ? prev : ruta));
  }, []);
  useEffect(() => { actualizarContexto(); }, [pageRows, actualizarContexto]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  if (!open) return null;

  const nSinTareas = rows.filter(r => (r.tareas_actuales || []).length === 0).length;
  // Fix IB: los ítems del filtro que ya tienen tareas (los que «Quitar tareas» deja sin tareas).
  const conTareasFiltro = filtered.filter(r => (r.tareas_actuales || []).length > 0);

  const quitarTareasFiltro = () => {
    const uids = new Set(conTareasFiltro.map(r => r.item_uid));
    if (uids.size === 0) return;
    setRows(prev => prev.map(r => uids.has(r.item_uid) ? { ...r, selected: "__clear__" } : r));
    setQuitados([...uids]);
    toast.info(`${uids.size} ítem${uids.size !== 1 ? "s" : ""} sin tareas al «Confirmar y guardar» — podés deshacerlo antes`);
  };

  const deshacerQuitar = () => {
    const uids = new Set(quitados || []);
    setRows(prev => prev.map(r => uids.has(r.item_uid) && r.selected === "__clear__" ? { ...r, selected: null } : r));
    setQuitados(null);
  };

  // Fix ID (2026-10-29): "sería bueno poder editar sugerencia antes de tomarla ... pequeños ajustes en una tarea o en
  // el porcentaje" (CEN). Abre el editor manual precargado con las tareas y % sugeridos; se guarda por el mismo camino.
  const ajustarSugerencia = (r) => {
    const filas = (r.sugerido?.tareas || []).map(t => ({ tarea_numero: String(t.tarea_numero), pct_ui: Math.round((Number(t.pct) || 0) * 10000) / 100 }));
    if (filas.length === 0) return;
    setManual(m => ({ ...m, [r.item_uid]: filas }));
    setRowSelected(r.item_uid, "__manual__");
  };

  const abrirReferencias = async () => {
    try {
      const { data } = await axios.get(`${API}/catalogo-tareas/referencias`, opts);
      setRefsPanel({ opciones: data.opciones || [], seleccion: new Set(data.seleccion || []), guardando: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudieron cargar los presupuestos de referencia");
    }
  };

  const guardarReferencias = async (ids) => {
    setRefsPanel(p => p && { ...p, guardando: true });
    try {
      await axios.put(`${API}/catalogo-tareas/referencias`, { budget_ids: ids }, opts);
      toast.success(ids.length ? `Las sugerencias ahora aprenden de ${ids.length} presupuesto${ids.length !== 1 ? "s" : ""}` : "Las sugerencias aprenden de todos los presupuestos con tareas");
      setRefsPanel(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar la selección");
      setRefsPanel(p => p && { ...p, guardando: false });
    }
  };
  const nAlta = rows.filter(r => r.match_status === "alta").length;

  const setRowSelected = (uid, value) => {
    setRows(prev => prev.map(r => r.item_uid === uid ? { ...r, selected: value } : r));
  };

  // Fix BY (2026-09-09): "tendría que poder copiar la configuración de un ítem y
  // pegarla en otro" (Edson, 09/09). En un cómputo de mil ítems la misma pareja de
  // tareas se repite decenas de veces y hoy hay que rearmarla fila por fila.
  // Se copia el desglose EFECTIVO: lo que está por guardarse si ya se eligió algo
  // en la fila, y si no, lo que el ítem ya tiene cargado.
  const tareasEfectivas = (r) => {
    if (r.selected === "__manual__") {
      return (manual[r.item_uid] || []).filter(f => f.tarea_numero !== "");
    }
    if (r.selected && String(r.selected).startsWith("t:")) {
      return [{ tarea_numero: String(r.selected).slice(2), pct_ui: 100 }];
    }
    if (r.selected === "__sug__") {   // Fix IB: las tareas de la sugerencia, con sus %
      return (r.sugerido?.tareas || []).map(t => ({ tarea_numero: String(t.tarea_numero), pct_ui: Math.round((Number(t.pct) || 0) * 10000) / 100 }));
    }
    const fuente = (r.selected && r.selected !== "__clear__")
      ? (templates.find(t => t.id === r.selected)?.tareas || [])
      : (r.tareas_actuales || []);
    return fuente.map(t => ({ tarea_numero: String(t.tarea_numero), pct_ui: Math.round((Number(t.pct) || 0) * 100) }));
  };

  const copiarFila = (r) => {
    const filas = tareasEfectivas(r);
    if (filas.length === 0) { toast.info("Ese ítem no tiene tareas para copiar"); return; }
    setCopiado({ desde: r.numero || r.descripcion, filas });
    toast.success(`Configuración de ${r.numero} copiada (${filas.length} tarea${filas.length !== 1 ? "s" : ""})`);
  };

  // Pegar deja la fila en modo manual con las mismas tareas y porcentajes: queda
  // editable antes de guardar y pasa por la misma validación de ~100% del backend.
  const pegarEn = (uids) => {
    if (!copiado || uids.length === 0) return;
    setManual(m => {
      const out = { ...m };
      uids.forEach(u => { out[u] = copiado.filas.map(f => ({ ...f })); });
      return out;
    });
    setRows(prev => prev.map(r => uids.includes(r.item_uid) ? { ...r, selected: "__manual__" } : r));
    toast.success(`Pegado en ${uids.length} ítem${uids.length !== 1 ? "s" : ""} — revisá y guardá`);
  };

  const crearTareaCatalogo = async (nombre) => {
    try {
      const { data } = await axios.post(`${API}/catalogo-tareas`, { nombre }, opts);
      const { data: cat } = await axios.get(`${API}/catalogo-tareas`, opts);
      setCatalogoTareas(cat || []);
      toast.success(data.ya_existia
        ? `Esa tarea ya estaba en el catálogo: ${data.numero}. ${data.nombre}`
        : `Tarea ${data.numero}. ${data.nombre} agregada al catálogo`);
      return data.numero;
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo agregar la tarea al catálogo");
      return null;
    }
  };

  // Fix BW (2026-09-09): `templatesOrdenados` existía solo para ordenar las 158
  // plantillas dentro del desplegable, que ya no las lista. Se saca porque una
  // constante declarada y no usada rompe el build de producción (no-unused-vars).
  // `templates` sigue en pie: lo usan el botón "Actualizar plantillas", el detalle
  // de la sugerencia y su nombre en el select.

  const recargarPlantillas = async () => {
    try {
      const [{ data: tmpl }, { data: cat }] = await Promise.all([
        axios.get(`${API}/catalogo-tareas/templates`, opts),
        axios.get(`${API}/catalogo-tareas`, opts),
      ]);
      setTemplates(tmpl || []);
      setCatalogoTareas(cat || []);
      toast.success(`${(tmpl || []).length} plantilla(s) de desglose cargada(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudieron actualizar las plantillas");
    }
  };

  const acceptHighConfidence = () => {
    const count = rows.filter(r => r.selected === null && r.sugerido && r.match_status === "alta").length;
    setRows(prev => prev.map(r => {
      if (r.selected === null && r.sugerido && r.match_status === "alta") return { ...r, selected: "__sug__" };   // Fix IB
      return r;
    }));
    toast.success(count > 0 ? `${count} sugerencia(s) de alta confianza aplicada(s) — revisá y guardá` : "No hay sugerencias de alta confianza pendientes");
  };

  // Fix IJ (2026-10-30): «Finalizar y aplicar» guarda el desglose manual de un solo ítem (mismo redondeo que el guardado
  // general: la última tarea absorbe el redondeo) y deja la fila en «Sin cambios» con sus tareas cargadas.
  const finalizarFila = async (r) => {
    const filas = (manual[r.item_uid] || []).filter(f => f.tarea_numero !== "" && Number(f.pct_ui) > 0);
    let acum = 0;
    const tareas = filas.map((f, i) => {
      const frac = i === filas.length - 1 ? Math.round((1 - acum) * 10000) / 10000 : Math.round((Number(f.pct_ui) / 100) * 10000) / 10000;
      acum = Math.round((acum + frac) * 10000) / 10000;
      return { tarea_numero: Number(f.tarea_numero), pct: frac };
    });
    if (!tareas.length) { toast.error("Elegí al menos una tarea"); return; }
    setFinalizando(r.item_uid);
    try {
      await axios.put(`${API}/budgets/${budgetId}/items-tareas`, { mappings: [{ item_uid: r.item_uid, tareas }] }, opts);
      onPatchItems({ [r.item_uid]: tareas });
      setRows(prev => prev.map(x => (x.item_uid === r.item_uid ? { ...x, tareas_actuales: tareas, selected: null } : x)));
      setManual(m => { const n = { ...m }; delete n[r.item_uid]; return n; });
      toast.success(`${r.numero}: tareas aplicadas`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo aplicar este ítem");
    } finally { setFinalizando(null); }
  };

  const handleSave = async () => {
    const mappings = rows
      .filter(r => r.selected !== null)
      .map(r => {
        // Fix BT (2026-09-09): sin cambios / borrar / plantilla / cargado a mano.
        // El backend espera `pct` como fracción 0-1 y exige que sume ~100%: se
        // convierte acá y la última fila absorbe el redondeo, así 3 tareas de 33%
        // suman exactamente 1 y no lo rechaza.
        let tareas;
        if (r.selected === "__clear__") {
          tareas = [];
        } else if (r.selected === "__manual__") {
          const filas = (manual[r.item_uid] || []).filter(f => f.tarea_numero !== "" && Number(f.pct_ui) > 0);
          const n = filas.length;
          let acum = 0;
          tareas = filas.map((f, i) => {
            const frac = i === n - 1 ? Math.round((1 - acum) * 10000) / 10000 : Math.round((Number(f.pct_ui) / 100) * 10000) / 10000;
            acum = Math.round((acum + frac) * 10000) / 10000;
            return { tarea_numero: Number(f.tarea_numero), pct: frac };
          });
        } else if (r.selected === "__sug__") {
          // Fix IB (2026-10-28): las tareas sugeridas, con los mismos % de la asignación previa (el backend valida ~100%).
          tareas = (r.sugerido?.tareas || []).map(t => ({ tarea_numero: Number(t.tarea_numero), pct: Number(t.pct) }));
        } else if (String(r.selected).startsWith("t:")) {
          // Fix BW (2026-09-09): una tarea del catálogo se lleva el 100% del fondo
          // de M.O. del ítem. El backend ya valida que la suma dé ~100%, así que
          // esto entra sin tocarle nada.
          tareas = [{ tarea_numero: Number(String(r.selected).slice(2)), pct: 1.0 }];
        } else {
          tareas = templates.find(t => t.id === r.selected)?.tareas || [];
        }
        return { item_uid: r.item_uid, tareas };
      });
    if (mappings.length === 0) { toast.info("No hay cambios para guardar"); return; }
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/budgets/${budgetId}/items-tareas`, { mappings }, opts);
      const byUid = {};
      mappings.forEach(m => { byUid[m.item_uid] = m.tareas; });
      onPatchItems(byUid);
      toast.success(`${data.updated} ítem${data.updated !== 1 ? "s" : ""} actualizado${data.updated !== 1 ? "s" : ""}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar el desglose de tareas");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="asignar-tareas-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      {/* Fix IB (2026-10-28): "mantener encabezados, filtros y acciones fijos al desplazar" (CEN). La ventana ya no se
          desplaza entera: título, aviso, barra y encabezados de columna quedan quietos y solo se desplazan las filas. */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white flex-shrink-0">
          <div>
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <Wand2 size={16} className="text-amber-500" /> Asignar desglose de Tareas por ítem
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {loading ? "Cargando sugerencias..." : `${nSinTareas} ítem${nSinTareas !== 1 ? "s" : ""} sin tareas · ${nAlta} con sugerencia de alta confianza`}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid="asignar-tareas-close-btn"><X size={16} /></button>
        </div>
        <div className="px-6 pt-4 pb-2 flex-1 min-h-0 flex flex-col gap-3" data-testid="asignar-tareas-cuerpo">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="animate-spin text-gray-400" size={28} /></div>
          ) : (
            <>
              <p className="flex-shrink-0 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Se comparan los ítems ya guardados del presupuesto. Guardá los cambios pendientes ("Guardar Avance") antes de usar esta herramienta.
              </p>
              {/* Fix ID: de dónde salen las sugerencias y el panel para elegir qué presupuestos enseñan. */}
              {referencias && (
                <div className="flex-shrink-0 text-[11px] text-gray-600 bg-[#EBF3FB] border border-[#2E6DA4]/20 rounded-lg px-3 py-1.5 flex items-center gap-2 flex-wrap"
                  data-testid="asignar-tareas-referencias">
                  <span>
                    <b>Sugerencias a partir de:</b>{" "}
                    {referencias.seleccion?.length
                      ? (referencias.fuentes || []).map(f => f.nombre).join(", ") || "los presupuestos elegidos"
                      : "todos los presupuestos y obras con tareas"}
                    {" "}({referencias.items} ítem{referencias.items !== 1 ? "s" : ""} con tareas)
                  </span>
                  <button type="button" onClick={refsPanel ? () => setRefsPanel(null) : abrirReferencias}
                    className="ml-auto flex-shrink-0 font-semibold text-[#2E6DA4] hover:underline" data-testid="asignar-tareas-referencias-elegir">
                    {refsPanel ? "Cerrar" : "Elegir presupuestos de referencia"}
                  </button>
                </div>
              )}
              {refsPanel && (
                <div className="flex-shrink-0 border border-[#2E6DA4]/30 rounded-lg px-3 py-2 bg-white max-h-40 overflow-y-auto" data-testid="asignar-tareas-referencias-panel">
                  {refsPanel.opciones.length === 0 && <p className="text-[11px] text-gray-400">Ningún presupuesto tiene tareas todavía.</p>}
                  {refsPanel.opciones.map(o => (
                    <label key={o.id} className="flex items-center gap-2 text-[11px] text-gray-700 py-0.5" data-testid={`asignar-tareas-referencia-${o.id}`}>
                      <input type="checkbox" checked={refsPanel.seleccion.has(o.id)}
                        onChange={e => setRefsPanel(p => { const sel = new Set(p.seleccion); if (e.target.checked) sel.add(o.id); else sel.delete(o.id); return { ...p, seleccion: sel }; })} />
                      <span className="font-semibold">{o.nombre}</span>{o.number ? <span className="text-gray-400">{o.number}</span> : null}
                      <span className="text-gray-500">· {o.items_presupuesto} ítem{o.items_presupuesto !== 1 ? "s" : ""} en el presupuesto{o.items_obra ? ` · ${o.items_obra} en la obra ${o.obra}` : ""}</span>
                    </label>
                  ))}
                  <div className="flex items-center gap-3 mt-1.5">
                    <button type="button" disabled={refsPanel.guardando} onClick={() => guardarReferencias([...refsPanel.seleccion])}
                      className="text-[11px] font-semibold text-white bg-[#2E6DA4] hover:bg-[#245A8A] px-3 py-1 rounded-lg disabled:opacity-60" data-testid="asignar-tareas-referencias-guardar">
                      Aprender solo de los marcados ({refsPanel.seleccion.size})
                    </button>
                    <button type="button" disabled={refsPanel.guardando} onClick={() => guardarReferencias([])}
                      className="text-[11px] font-semibold text-gray-600 hover:underline disabled:opacity-60" data-testid="asignar-tareas-referencias-todos">
                      Aprender de todos
                    </button>
                  </div>
                </div>
              )}
              <div className="flex-shrink-0 flex items-center gap-2 flex-wrap" data-testid="asignar-tareas-barra">
                <input value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} placeholder="Buscar por N° o descripción..."
                  className="border border-gray-200 rounded-lg px-3 py-1.5 text-xs flex-1 min-w-[180px]" data-testid="asignar-tareas-search" />
                <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(0); }}
                  className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white" data-testid="asignar-tareas-filter">
                  <option value="sin_tareas">Sin tareas asignadas</option>
                  <option value="con_tareas">Con tareas asignadas</option>
                  <option value="todos">Todos</option>
                  <option value="alta">Sugerencia alta</option>
                  <option value="media">Sugerencia media</option>
                  <option value="baja">Sugerencia baja</option>
                </select>
                {/* Fix BT (2026-09-09): "el desplegable hay que actualizar" — si se
                    importa un Excel de desglose con esta ventana abierta, este botón
                    vuelve a leer las plantillas sin perder lo que ya se eligió. */}
                <button onClick={recargarPlantillas}
                  className="flex-shrink-0 flex items-center gap-1.5 border border-gray-300 hover:border-[#2E6DA4] text-gray-600 px-3 py-1.5 rounded-lg text-xs font-semibold"
                  data-testid="asignar-tareas-recargar-plantillas">
                  <RefreshCw size={13} /> Actualizar plantillas ({templates.length})
                </button>
                <button onClick={acceptHighConfidence}
                  className="flex-shrink-0 flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg text-xs font-semibold"
                  data-testid="asignar-tareas-accept-high-btn">
                  <Check size={13} /> Aceptar sugerencias de alta confianza
                </button>
                {/* Fix IB: quitar en bloque las tareas de los ítems del filtro que las tienen. */}
                <button onClick={quitarTareasFiltro} disabled={conTareasFiltro.length === 0}
                  title="Deja sin tareas a los ítems del filtro que las tienen. Se borran al «Confirmar y guardar»."
                  className="flex-shrink-0 flex items-center gap-1.5 border border-red-300 text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-40"
                  data-testid="asignar-tareas-quitar-todas">
                  <Trash2 size={13} /> Quitar tareas ({conTareasFiltro.length})
                </button>
              </div>
              {quitados && (
                <div className="flex-shrink-0 flex items-center gap-2 flex-wrap text-[11px] bg-red-50 border border-red-200 rounded-lg px-3 py-1.5"
                  data-testid="asignar-tareas-quitados">
                  <span className="text-red-700 font-semibold">
                    {quitados.length} ítem{quitados.length !== 1 ? "s" : ""} quedan sin tareas al «Confirmar y guardar».
                  </span>
                  <button onClick={deshacerQuitar} className="ml-auto flex-shrink-0 font-semibold text-[#2E6DA4] hover:underline"
                    data-testid="asignar-tareas-quitados-deshacer">Deshacer</button>
                </div>
              )}
              {/* Fix BY (2026-09-09): el portapapeles de configuraciones. El pegado
                  masivo trabaja sobre el FILTRO completo, no sobre la página: con
                  la búsqueda en "TOMACORRIENTE" se resuelven los 40 de una vez. */}
              {copiado && (
                <div className="flex-shrink-0 flex items-center gap-2 flex-wrap text-[11px] bg-[#EBF3FB] border border-[#2E6DA4]/30 rounded-lg px-3 py-1.5"
                  data-testid="asignar-tareas-portapapeles">
                  <span className="font-semibold text-[#1A4B7A] flex-shrink-0">Copiado de {copiado.desde}:</span>
                  <span className="text-gray-600">
                    {copiado.filas
                      .map(f => `${f.tarea_numero}. ${(catalogoTareas.find(c => String(c.numero) === String(f.tarea_numero)) || {}).nombre || ""} ${f.pct_ui}%`)
                      .join(" · ")}
                  </span>
                  <button onClick={() => pegarEn(filtered.map(r => r.item_uid))}
                    className="ml-auto flex-shrink-0 font-semibold text-[#2E6DA4] hover:underline"
                    data-testid="asignar-tareas-pegar-visibles">
                    Pegar en los {filtered.length} ítems del filtro
                  </button>
                  <button onClick={() => setCopiado(null)}
                    className="flex-shrink-0 font-semibold text-gray-400 hover:text-gray-600"
                    data-testid="asignar-tareas-copiado-limpiar">Quitar</button>
                </div>
              )}
              <div ref={listaRef} onScroll={actualizarContexto} className="flex-1 min-h-0 overflow-y-auto border border-gray-200 rounded-xl" data-testid="asignar-tareas-lista">
                <table className="w-full text-xs">
                  <thead className="bg-[#EBF3FB] sticky top-0 z-10" data-testid="asignar-tareas-encabezado">
                    <tr>
                      <th className="text-left px-3 py-2 font-bold text-[#1A4B7A] uppercase tracking-wide bg-[#EBF3FB]">Ítem</th>
                      <th className="text-left px-3 py-2 font-bold text-[#1A4B7A] uppercase tracking-wide bg-[#EBF3FB]">Sugerencia</th>
                      {/* Fix BW (2026-09-09): ya no se elige una plantilla de boca
                          sino una tarea del catálogo, así que el título lo dice. */}
                      <th className="text-left px-3 py-2 font-bold text-[#1A4B7A] uppercase tracking-wide bg-[#EBF3FB]">Tareas a aplicar</th>
                    </tr>
                    {contexto.length > 0 && (
                      <tr data-testid="asignar-tareas-contexto">
                        <td colSpan={3} className="px-3 py-1 bg-[#DCE9F6] border-t border-[#2E6DA4]/20">
                          {contexto.map((g, i) => (
                            <p key={`${g.numero}-${i}`} className="text-[11px] font-bold text-[#1A4B7A] uppercase leading-5 truncate" style={{ paddingLeft: i * 14 }}
                              data-testid={`asignar-tareas-contexto-${i}`}>{g.numero} {g.descripcion}</p>
                          ))}
                        </td>
                      </tr>
                    )}
                  </thead>
                  <tbody>
                    {pageRows.map((r, idx) => {
                      // Fix IK: los encabezados que empiezan con esta fila (en la primera de la página, todos los suyos).
                      const ant = idx > 0 ? (pageRows[idx - 1].encabezados || []) : [];
                      const cab = r.encabezados || [];
                      let desde = 0;
                      while (idx > 0 && desde < cab.length && desde < ant.length && cab[desde].numero === ant[desde].numero && cab[desde].descripcion === ant[desde].descripcion) desde += 1;
                      return (
                      <Fragment key={r.item_uid}>
                      {cab.slice(desde).map((g, j) => (
                        <tr key={`g-${desde + j}-${g.numero}`} className="bg-[#F3F7FB] border-t border-[#2E6DA4]/15" data-testid={`asignar-tareas-grupo-${g.numero}`}
                          data-ruta={JSON.stringify(cab.slice(0, desde + j + 1))}>
                          <td colSpan={3} className="py-1.5 pr-3 text-[11px] font-bold text-[#1A4B7A] uppercase" style={{ paddingLeft: 12 + (desde + j) * 14 }}>{g.numero} {g.descripcion}</td>
                        </tr>
                      ))}
                      <tr className="border-t border-gray-100" data-testid={`asignar-tareas-row-${r.item_uid}`} data-ruta={JSON.stringify(cab)}>
                        {/* Fix AI (2026-09-04): descripción completa, en varias líneas. */}
                        <td className="px-3 py-2 font-semibold text-gray-800 max-w-[220px] whitespace-normal break-words" title={r.descripcion}>
                          {r.numero} — {r.descripcion}
                          {/* Fix BY (2026-09-09): "que no me oculte los que ya tienen
                              tareas, que muestre lo que tienen cargado y me deje
                              editarlo" (Edson, 09/09). Con el filtro en Todos la fila
                              ya venía, pero no decía nada: el desplegable arranca en
                              "Sin cambios" y el desglose guardado quedaba invisible.
                              Los nombres salen del mismo catálogo que usa la pestaña
                              Tareas de la obra, así que dicen exactamente lo mismo. */}
                          {(r.tareas_actuales || []).length > 0 ? (
                            <p className="mt-1 font-normal text-[10px] text-emerald-700 leading-tight" data-testid={`asignar-tareas-actuales-${r.item_uid}`}>
                              Ya cargado: {(r.tareas_actuales || [])
                                .map(t => `${t.tarea_numero}. ${(catalogoTareas.find(c => c.numero === t.tarea_numero) || {}).nombre || ""} ${Math.round((Number(t.pct) || 0) * 100)}%`)
                                .join(" · ")}
                            </p>
                          ) : (
                            <p className="mt-1 font-normal text-[10px] text-gray-400" data-testid={`asignar-tareas-actuales-${r.item_uid}`}>
                              Sin tareas cargadas
                            </p>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {r.sugerido ? (
                            <div data-testid={`asignar-tareas-sugerencia-${r.item_uid}`}>
                              {/* Fix IB: tareas del desplegable con su %, y aparte cuánto se parece la descripción. */}
                              <span className={`inline-block px-2 py-0.5 rounded-lg text-[10px] font-bold leading-snug ${scoreBadge(r.match_status)}`}>
                                {textoTareas(r.sugerido.tareas, catalogoTareas)}
                              </span>
                              <p className="mt-0.5 text-[10px] text-gray-400 leading-tight">
                                parecido {Math.round(r.sugerido.score * 100)}% a «{r.sugerido.descripcion}»{r.sugerido.presupuesto ? ` (${r.sugerido.presupuesto})` : ""}
                              </p>
                              {/* Fix ID: ajustar tarea o % antes de aplicar (abre el editor manual con la sugerencia cargada). */}
                              {r.selected !== "__manual__" && (
                                <button type="button" onClick={() => ajustarSugerencia(r)}
                                  className="mt-0.5 text-[10px] font-semibold text-[#2E6DA4] hover:underline"
                                  data-testid={`asignar-tareas-ajustar-${r.item_uid}`}>Ajustar antes de aplicar</button>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-300 text-[10px]">sin sugerencia</span>
                          )}
                        </td>
                        <td className="px-3 py-2 align-top min-w-[280px]">
                          {/* Fix BT (2026-09-09): el nombre que se muestra es el del
                              Excel (descripcion_display), el mismo que ya usa la
                              columna Sugerencia; antes se veía la clave normalizada
                              en mayúsculas y no parecían las mismas plantillas. */}
                          <select value={r.selected ?? ""} onChange={e => {
                              const v = e.target.value || null;
                              setRowSelected(r.item_uid, v);
                              if (v === "__manual__" && !manual[r.item_uid]) {
                                // Arranca con las tareas que el ítem ya tiene, o con una fila vacía.
                                const actuales = (r.tareas_actuales || []).map(t => ({
                                  tarea_numero: String(t.tarea_numero), pct_ui: Math.round((Number(t.pct) || 0) * 100),
                                }));
                                setManual(m => ({ ...m, [r.item_uid]: actuales.length ? actuales : [{ tarea_numero: "", pct_ui: "" }] }));
                              }
                            }}
                            className="w-full border border-gray-200 rounded-lg px-2 py-1 text-[11px] bg-white"
                            data-testid={`asignar-tareas-select-${r.item_uid}`}>
                            <option value="">— Sin cambios —</option>
                            {/* Fix BY (2026-09-09): en un ítem que ya tiene desglose,
                                esta opción no define nada nuevo: abre lo que ya está
                                cargado para corregirlo (el editor se precarga con
                                `tareas_actuales` desde el Fix BT). El título lo dice. */}
                            <option value="__manual__">
                              {(r.tareas_actuales || []).length > 0 ? "✎ Editar las tareas cargadas…" : "✎ Definir tareas a mano…"}
                            </option>
                            <option value="__clear__">Sin asignar (borrar tareas)</option>
                            {r.sugerido && <option value="__sug__">Usar la sugerencia</option>}
                            {/* Fix BW (2026-09-09): "tendrían que ser directamente las
                                tareas que salen cuando le damos definir tareas a mano"
                                (Edson, 09/09). Las 158 plantillas eran tipos de ítem
                                —"Boca de anafe 4 hornallas"— y no decían nada frente a
                                un ítem como "Gestiones administrativas en ANDE". */}
                            <optgroup label="Aplicar una tarea al 100%">
                              {catalogoTareas.map(c => (
                                <option key={c.numero} value={`t:${c.numero}`}>{c.numero}. {c.nombre}</option>
                              ))}
                            </optgroup>
                            {/* La sugerencia de alta confianza sigue trayendo una plantilla.
                                Si su id no figura como opción, React marca la primera y la
                                fila miente: dice "Sin cambios" con una plantilla adentro
                                (el mismo problema de la unidad en el Fix AY). Se agrega
                                solo en la fila que la tiene elegida. */}
                            {r.selected && !String(r.selected).startsWith("t:")
                              && r.selected !== "__manual__" && r.selected !== "__clear__" && r.selected !== "__sug__" && (
                              <optgroup label="Sugerencia aceptada">
                                <option value={r.selected}>
                                  {(templates.find(t => t.id === r.selected) || {}).descripcion_display
                                    || (templates.find(t => t.id === r.selected) || {}).descripcion_patron
                                    || "Plantilla sugerida"}
                                </option>
                              </optgroup>
                            )}
                          </select>
                          {/* Fix BW (2026-09-09): una tarea suelta se explica sola, así
                              que el detalle queda para las plantillas de la sugerencia. */}
                          {r.selected && String(r.selected).startsWith("t:") && (
                            <p className="mt-1 text-[10px] text-emerald-700 leading-tight" data-testid={`asignar-tareas-detalle-${r.item_uid}`}>
                              Se aplica esta unica tarea al 100% del fondo de mano de obra del item.
                            </p>
                          )}
                          {/* Qué aplica la plantilla elegida, para no confirmar a ciegas. */}
                          {r.selected === "__sug__" && (
                            <p className="mt-1 text-[10px] text-emerald-700 leading-tight" data-testid={`asignar-tareas-detalle-${r.item_uid}`}>
                              Se aplica: {textoTareas(r.sugerido?.tareas, catalogoTareas)}
                            </p>
                          )}
                          {r.selected && !String(r.selected).startsWith("t:")
                            && r.selected !== "__manual__" && r.selected !== "__clear__" && r.selected !== "__sug__" && (
                            <p className="mt-1 text-[10px] text-gray-500 leading-tight" data-testid={`asignar-tareas-detalle-${r.item_uid}`}>
                              {(templates.find(t => t.id === r.selected)?.tareas || [])
                                .map(t => `${t.tarea_numero}. ${(catalogoTareas.find(c => c.numero === t.tarea_numero) || {}).nombre || ""} ${Math.round((Number(t.pct) || 0) * 100)}%`)
                                .join(" · ") || "Esta plantilla no tiene tareas cargadas."}
                            </p>
                          )}
                          {r.selected === "__manual__" && (
                            <EditorTareasManual
                              catalogo={catalogoTareas}
                              valor={manual[r.item_uid]}
                              onChange={filas => setManual(m => ({ ...m, [r.item_uid]: filas }))}
                              onNuevaTarea={crearTareaCatalogo}
                              onFinalizar={() => finalizarFila(r)}
                              finalizando={finalizando === r.item_uid}
                            />
                          )}
                          {/* Fix BY (2026-09-09): copiar de acá y pegar en cualquier
                              otra fila, o en todo el filtro desde la barra de arriba. */}
                          <div className="mt-1 flex items-center gap-3">
                            <button type="button" onClick={() => copiarFila(r)}
                              className="text-[10px] font-semibold text-[#2E6DA4] hover:underline"
                              data-testid={`asignar-tareas-copiar-${r.item_uid}`}>Copiar esta configuración</button>
                            {copiado && (
                              <button type="button" onClick={() => pegarEn([r.item_uid])}
                                className="text-[10px] font-semibold text-emerald-700 hover:underline"
                                data-testid={`asignar-tareas-pegar-${r.item_uid}`}>Pegar acá</button>
                            )}
                          </div>
                        </td>
                      </tr>
                      </Fragment>
                      );
                    })}
                    {pageRows.length === 0 && (
                      <tr><td colSpan={3} className="px-3 py-6 text-center text-gray-400">Sin resultados para este filtro.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="flex-shrink-0 flex items-center justify-center gap-3 text-xs text-gray-500">
                  <button disabled={page === 0} onClick={() => setPage(p => p - 1)} className="px-2 py-1 border rounded disabled:opacity-40">Anterior</button>
                  Página {page + 1} de {totalPages}
                  <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} className="px-2 py-1 border rounded disabled:opacity-40">Siguiente</button>
                </div>
              )}
            </>
          )}
        </div>
        <div className="px-6 pb-6 pt-2 flex gap-3 flex-shrink-0">
          <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50">Cancelar</button>
          <button onClick={handleSave} disabled={saving || loading}
            className="flex-1 flex items-center justify-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-60"
            data-testid="asignar-tareas-save-btn">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <ListChecks size={15} />}
            {saving ? "Guardando..." : "Confirmar y guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}
